<script setup lang="ts">
import { assetLink } from "@/common/util"
import type { Article } from "@/model/backend/article"
import CartListItem from "@/shop/components/checkout/CartListItem.vue"
import { useShopAppConfig } from "@/shop/composables"
import { type CartCoupon, useCartStore } from "@/shop/store/useCartStore"
import { isPreparedArticleType } from "@/model/prepared-types"
import { $currency } from "@/vf"
import { computed, watch } from "vue"
import blossomImage from "@/shop/assets/blossom.png"
import PriceSpinner from "./PriceSpinner.vue"
/*─────────────────────────────────────┐
│   props                              │
└─────────────────────────────────────*/
const props = withDefaults(
    defineProps<{
        error?: {
            errorType?: "pendingOrder" | "file" | "invalidZipForCarrierService" | "outOfStock"
            articles?: string[]
        }
        isCart?: boolean
    }>(),
    {
        error: undefined,
        isCart: false,
    },
)
/*─────────────────────────────────────┐
│   composables                        │
└─────────────────────────────────────*/
const cartStore = useCartStore()

const { appConfig } = useShopAppConfig()

/*─────────────────────────────────────┐
│   coupon auto-apply                  │
└─────────────────────────────────────*/
// Only the cart view (isCart) drives auto-apply of free-article coupons —
// the checkout instance of CartList must never touch coupon state. Re-evaluate
// whenever coupon availability changes (a coupon appears/disappears) and once
// immediately on mount (covers freebies added from a product page before
// opening the cart).
watch(
    () => cartStore.coupons.map(coupon => coupon.id).join("|"),
    () => {
        if (props.isCart) {
            cartStore.applyEligibleFreeArticleCoupons()
        }
    },
    { immediate: true },
)

/*─────────────────────────────────────┐
│   computed                           │
└─────────────────────────────────────*/
const showDeliveryTimeWarning = computed(() => {
    const hasPreparedType = cartStore.items.some(item => isPreparedArticleType(item.article.type))

    const hasCannabisOrEssence = cartStore.items.some(
        item => item.article.type === "cannabis" || item.article.type === "essence",
    )

    return hasPreparedType && hasCannabisOrEssence
})

const sortedCartStoreItems = computed(() =>
    [...cartStore.items].sort((a, b) => {
        const typeA = a.article.type ?? ""
        const typeB = b.article.type ?? ""
        return typeA.localeCompare(typeB)
    }),
)

function getCouponArticles(coupon: CartCoupon): { article: Article; quantity: number }[] {
    const result: { article: Article; quantity: number }[] = []
    for (const entry of coupon.addArticles) {
        const article = appConfig.value.articles.find(a => a.id === entry.articleId)
        if (article) {
            result.push({ article, quantity: entry.quantity })
        }
    }
    return result
}

function getFirstArticle(coupon: CartCoupon): Article | undefined {
    return getCouponArticles(coupon)[0]?.article
}

function toggleCoupon(coupon: CartCoupon) {
    const newActive = !coupon.active

    if (newActive) {
        for (const { article, quantity } of getCouponArticles(coupon)) {
            const cartItem = cartStore.getCartItemForArticle(article)
            if (cartItem) {
                cartItem.amount = quantity
            } else {
                cartStore.addToCart(article, quantity, false)
            }
        }
    }

    cartStore.setCouponActive(coupon.id, newActive)
}
</script>

<template>
    <div v-if="!cartStore.items.length" class="py-5 text-center text-muted">
        Du hast noch keine Artikel in deinem Warenkorb
    </div>

    <div v-if="showDeliveryTimeWarning" class="alert alert-warning mb-2 mt-4">
        <b>Bitte beachte:</b> Du hast "CANN24 Readymade", "CANN24 Milled", "CANN24 Smalls" und/oder "CANN24 Xtract" im
        Warenkorb. Da deine Bestellung auch Blüten und/oder Extrakte enthält, richtet sich die Bearbeitungszeit nach den
        Produkten mit der längeren Bearbeitungszeit.
    </div>

    <template v-for="item in sortedCartStoreItems" :key="item">
        <CartListItem v-bind="{ item, isCart, error }" />
    </template>

    <table class="table table-borderless my-3">
        <tr v-if="isCart && cartStore.coupons.length">
            <td colspan="5">
                <div class="small text-muted mb-2">Verfügbare Coupons</div>
                <div class="coupon-cards">
                    <button
                        v-for="coupon in cartStore.coupons"
                        :key="coupon.id"
                        type="button"
                        class="coupon-card"
                        :class="{ 'coupon-card--active': coupon.active }"
                        @click="toggleCoupon(coupon)"
                    >
                        <div v-if="getFirstArticle(coupon)" class="coupon-card__image-wrap">
                            <img
                                v-if="getFirstArticle(coupon)?.imageName"
                                class="coupon-card__image"
                                :src="assetLink('/uploads/media/' + getFirstArticle(coupon)?.imageName)"
                                alt=""
                            />
                            <img v-else class="coupon-card__image" :src="blossomImage" alt="" />
                        </div>
                        <div class="coupon-card__text">
                            <div class="coupon-card__title">{{ coupon.cartActionButtonLabel }}</div>
                            <div class="coupon-card__state">
                                {{ coupon.active ? "Aktiviert" : "Zum Hinzufügen klicken" }}
                            </div>
                        </div>
                    </button>
                </div>
            </td>
        </tr>
        <template v-for="(item, $index) in cartStore.serverItems" :key="$index">
            <tr v-if="item.type !== 'coupon'">
                <td colspan="4" class="text-right">
                    {{ item.name }}
                </td>
                <td class="text-right">
                    <div
                        v-if="
                            item.originalPrice !== undefined &&
                            item.originalPrice !== null &&
                            item.originalTotalPrice !== undefined &&
                            item.originalTotalPrice !== null &&
                            item.originalTotalPrice !== item.totalPrice
                        "
                        class="small text-muted"
                    >
                        <s>{{ $currency(item.originalTotalPrice) }}</s>
                    </div>
                    {{ $currency(item.totalPrice) }}
                </td>
            </tr>
        </template>
        <template v-for="(item, $index) in cartStore.serverItems" :key="`coupon-${$index}`">
            <tr v-if="item.type === 'coupon' && item.totalPrice !== 0" class="text-success">
                <td colspan="4" class="text-right">{{ item.name }}</td>
                <td class="text-right">{{ $currency(item.totalPrice) }}</td>
            </tr>
        </template>
        <template v-if="(cartStore.totalTax ?? 0) > 0">
            <tr>
                <td colspan="4" class="text-right">Gesamtsumme ohne MwSt.</td>
                <td class="text-right">
                    <PriceSpinner
                        :price="
                            cartStore.totalPrice === undefined
                                ? undefined
                                : cartStore.totalPrice - (cartStore.totalTax ?? 0)
                        "
                    ></PriceSpinner>
                </td>
            </tr>
            <tr v-for="item in cartStore.totalTaxes" :key="item.vat">
                <td colspan="4" class="text-right">zzgl. {{ item.vat }}% MwSt.</td>
                <td class="text-right">
                    <PriceSpinner :price="cartStore.totalPrice === undefined ? undefined : item.tax"></PriceSpinner>
                </td>
            </tr>
        </template>
        <tr>
            <td colspan="5"></td>
        </tr>
        <tr v-if="cartStore.totalPaymentPrice !== cartStore.totalPrice">
            <td colspan="4" class="text-right">Zwischensumme</td>
            <td class="text-right" style="min-width: 30%">
                <PriceSpinner :price="cartStore.totalPrice"></PriceSpinner>
            </td>
        </tr>
        <tr v-if="cartStore.usedBalance !== 0" class="text-primary">
            <td colspan="4" class="text-right">Verwendetes Guthaben</td>
            <td class="text-right">
                {{ $currency(cartStore.usedBalance) }}
            </td>
        </tr>
        <tr class="font-weight-bold">
            <td colspan="4" class="text-right">Gesamtsumme</td>
            <td class="text-right" style="min-width: 30%">
                <PriceSpinner :price="cartStore.totalPaymentPrice"></PriceSpinner>
            </td>
        </tr>
        <tr v-if="isCart">
            <td colspan="5" class="text-right">ggf. zzgl. Versandkosten</td>
        </tr>

        <!-- the prescription fee only applies to articles covered by the Kostenträger (essences) -->
        <template v-if="appConfig.user.payType === 'public_insurance' && isCart && cartStore.containsLiquidItems">
            <tr>
                <td colspan="5" class="text-right">Kosten: {{ $currency(1000) }} Gebühr pro Rezept.</td>
            </tr>
        </template>
    </table>
</template>

<style scoped>
.coupon-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.coupon-card {
    display: flex;
    align-items: center;
    width: 220px;
    border: 1px solid #ced4da;
    border-radius: 0.5rem;
    background: #fff;
    padding: 0.5rem;
    text-align: left;
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease,
        background-color 0.2s ease;
}

.coupon-card:hover {
    border-color: #8bbf7c;
    box-shadow: 0 0 0 2px rgba(62, 140, 40, 0.12);
}

.coupon-card--active {
    border-color: #3e8c28;
    background: #f2f8f0;
}

.coupon-card__image-wrap {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    margin-right: 0.5rem;
    border-radius: 0.35rem;
    overflow: hidden;
    background: #f5f5f5;
}

.coupon-card__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.coupon-card__text {
    min-width: 0;
}

.coupon-card__title {
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.2;
}

.coupon-card__state {
    margin-top: 0.1rem;
    color: #6c757d;
    font-size: 0.75rem;
}
</style>
